const DEFAULT_BASELINES = {
  0: 0.5,
  3: 0.6,
  7: 0.65,
  14: 0.7,
}

export const TOTAL_FALLBACK_STANDARD_MODE = 'highlight'

const DEFAULT_MEDIA_SOURCE = 'total'
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** Group pid lookup order for aggregate views only. Concrete pids never fall back to groups. */
const GROUP_BASELINE_CANDIDATES = {
  total1: ['total1'],
  total1_1: ['total1_1', 'total1'],
  total1_2: ['total1_2', 'total1'],
  total2: ['total2'],
  total3: ['total3'],
  total4: ['total4'],
}

function getDefaultBaseline(targetDay) {
  if (Object.prototype.hasOwnProperty.call(DEFAULT_BASELINES, targetDay)) {
    return DEFAULT_BASELINES[targetDay]
  }
  return 0
}

function normalizeMediaSource(mediaSource) {
  return String(mediaSource || DEFAULT_MEDIA_SOURCE).trim() || DEFAULT_MEDIA_SOURCE
}

function isDateMap(value) {
  return (
    value &&
    typeof value === 'object' &&
    Object.keys(value).some((key) => DATE_KEY_PATTERN.test(key))
  )
}

/**
 * Resolve baseline pid candidates for the current filter.
 * Aggregate group pids may fall back to parent groups; concrete pids stay exact.
 * @param {string|number|null} pid
 * @returns {string[]}
 */
export function resolveBaselinePidCandidates(pid) {
  if (pid === null || pid === undefined || pid === '') {
    return []
  }

  const pidStr = String(pid)
  if (Object.prototype.hasOwnProperty.call(GROUP_BASELINE_CANDIDATES, pidStr)) {
    return [...GROUP_BASELINE_CANDIDATES[pidStr]]
  }

  const parsed = parsePid(pidStr)
  return parsed.basePid ? [parsed.basePid] : []
}

function getPidBaselineMap(countryBaselines, basePid, packageName = null) {
  if (String(basePid) === '36') {
    if (packageName === 'main' || packageName === 'total') packageName = null
    if (packageName) return countryBaselines[`${basePid}-${packageName}`] || null
  }
  let pidBaselines = null
  if (packageName) {
    const packageKey = `${basePid}-${packageName}`
    pidBaselines = countryBaselines[packageKey]
  }

  if (!pidBaselines) {
    pidBaselines = countryBaselines[basePid]
  }

  return pidBaselines || null
}

/**
 * Media maps in priority order: requested channel first, then total fallback.
 * Callers must stop at the first map that yields an applicable value.
 */
function getMediaBaselineMaps(pidBaselines, mediaSource = DEFAULT_MEDIA_SOURCE) {
  if (!pidBaselines) {
    return []
  }

  if (isDateMap(pidBaselines)) {
    return [
      {
        baselineMap: pidBaselines,
        sourceMediaSource: DEFAULT_MEDIA_SOURCE,
        isFallback: normalizeMediaSource(mediaSource) !== DEFAULT_MEDIA_SOURCE,
      },
    ]
  }

  const effectiveMediaSource = normalizeMediaSource(mediaSource)
  const maps = []

  if (pidBaselines[effectiveMediaSource]) {
    maps.push({
      baselineMap: pidBaselines[effectiveMediaSource],
      sourceMediaSource: effectiveMediaSource,
      isFallback: false,
    })
  }

  if (
    effectiveMediaSource !== DEFAULT_MEDIA_SOURCE &&
    pidBaselines[DEFAULT_MEDIA_SOURCE]
  ) {
    maps.push({
      baselineMap: pidBaselines[DEFAULT_MEDIA_SOURCE],
      sourceMediaSource: DEFAULT_MEDIA_SOURCE,
      isFallback: true,
    })
  }

  return maps
}

/**
 * 国家 baseline 按优先级排列：先请求国家，再共享的 `all` 兜底。
 * 调用方必须在第一个有可用标准的来源停止，保证具体国家的 total 仍优先于 `all` 渠道。
 */
function getCountryBaselineSources(dayBaselines, country) {
  if (!dayBaselines) return []

  const sources = []
  if (dayBaselines[country]) {
    sources.push({
      countryBaselines: dayBaselines[country],
      isCountryFallback: false,
    })
  }

  if (country !== 'all' && dayBaselines.all) {
    sources.push({
      countryBaselines: dayBaselines.all,
      isCountryFallback: true,
    })
  }

  return sources
}

function getLatestBaselineEntry(baselineMap) {
  let latestStartDate = ''
  let value = null

  for (const startDate in baselineMap) {
    if (startDate > latestStartDate) {
      latestStartDate = startDate
      value = baselineMap[startDate]
    }
  }

  return { latestStartDate, value }
}

function getBaselineEntryAtDate(baselineMap, date) {
  let value = null
  let latestStartDate = ''
  let latestStartDateNum = -1
  const currentDateNum = parseInt(String(date || '').replace(/-/g, ''), 10)

  if (Number.isNaN(currentDateNum)) {
    return { value: null, startDate: '' }
  }

  for (const startDate in baselineMap) {
    const startDateNum = parseInt(startDate.replace(/-/g, ''), 10)

    if (startDateNum <= currentDateNum && startDateNum > latestStartDateNum) {
      latestStartDateNum = startDateNum
      latestStartDate = startDate
      value = baselineMap[startDate]
    }
  }

  return { value, startDate: latestStartDate }
}

function emptyBaselineResult(targetDay) {
  return {
    value: getDefaultBaseline(targetDay),
    isDefault: true,
    sourcePid: null,
    sourceMediaSource: null,
    isGroupFallback: false,
    isMediaFallback: false,
    isCountryFallback: false,
  }
}

/**
 * Latest startDate scoped to the current pid candidates and media (plus total fallback).
 * Avoids marking a channel-owned old standard as stale just because another pid/media is newer.
 */
export function getScopedLatestStartDate(
  baselines,
  targetDay,
  country,
  pid,
  packageName = null,
  mediaSource = DEFAULT_MEDIA_SOURCE,
) {
  if (!baselines || !baselines[targetDay]) {
    return ''
  }

  const countrySources = getCountryBaselineSources(baselines[targetDay], country)
  const candidates = resolveBaselinePidCandidates(pid)
  const parsed = parsePid(pid)
  const effectivePackageName =
    packageName !== null ? packageName : parsed.packageName
  let latest = ''

  for (const { countryBaselines } of countrySources) {
    let hasScopedBaseline = false

    for (const candidatePid of candidates) {
      const usePackage =
        ['14', '36'].includes(String(candidatePid)) || String(candidatePid).startsWith('14-')
          ? effectivePackageName
          : null
      const pidBaselines = getPidBaselineMap(
        countryBaselines,
        candidatePid,
        usePackage,
      )
      const mediaMaps = getMediaBaselineMaps(pidBaselines, mediaSource)
      for (const { baselineMap } of mediaMaps) {
        if (!isDateMap(baselineMap)) continue
        hasScopedBaseline = true
        for (const startDate in baselineMap) {
          if (startDate > latest) latest = startDate
        }
      }
    }

    if (hasScopedBaseline) return latest
  }

  return latest
}

/**
 * 获取整个 baseline 数据中的全局最新 startDate
 * @param {object} baselines - 从后端获取的完整 baseline 数据
 * @param {number} targetDay - 目标天数 (0, 3, 7, 14, 30, ...)
 * @returns {string} - 全局最新 startDate (YYYY-MM-DD)，若无则返回空字符串
 */
export function getGlobalLatestStartDate(baselines, targetDay) {
  if (!baselines || !baselines[targetDay]) return ''

  let globalLatest = ''
  const dayBaselines = baselines[targetDay]

  for (const country in dayBaselines) {
    const countryBaselines = dayBaselines[country]
    for (const pidKey in countryBaselines) {
      const pidBaselines = countryBaselines[pidKey]
      const maps = isDateMap(pidBaselines)
        ? [pidBaselines]
        : Object.values(pidBaselines || {})
      maps.forEach((baselineMap) => {
        if (!isDateMap(baselineMap)) return
        for (const startDate in baselineMap) {
          if (startDate > globalLatest) {
            globalLatest = startDate
          }
        }
      })
    }
  }

  return globalLatest
}

/**
 * 获取指定 pid/country/targetDay 的最新 DR baseline 值
 * Aggregate group pids may fall back to parent groups; concrete pids never use group standards.
 * 渠道优先请求渠道，其次 total；国家优先具体国家，其次 `all`。
 * 过期检查只限定在最终选中的国家来源内。
 * @returns {{ value: number, isDefault: boolean, sourcePid: string|null, sourceMediaSource: string|null, isGroupFallback: boolean, isMediaFallback: boolean, isCountryFallback: boolean }}
 */
export function getDrBaseline(
  baselines,
  targetDay,
  country,
  pid,
  packageName = null,
  mediaSource = DEFAULT_MEDIA_SOURCE,
) {
  const parsed = parsePid(pid)
  const effectivePackageName =
    packageName !== null ? packageName : parsed.packageName
  if (parsed.basePid === '36' && effectivePackageName === 'bus') {
    return { ...emptyBaselineResult(targetDay), value: null }
  }
  const candidates = resolveBaselinePidCandidates(pid)

  if (
    candidates.length === 0 ||
    !baselines ||
    !baselines[targetDay]
  ) {
    return emptyBaselineResult(targetDay)
  }

  const countrySources = getCountryBaselineSources(baselines[targetDay], country)
  const scopedLatestDate = getScopedLatestStartDate(
    baselines,
    targetDay,
    country,
    pid,
    effectivePackageName,
    mediaSource,
  )
  const requestedPid = String(pid)

  for (const { countryBaselines, isCountryFallback } of countrySources) {
    for (let i = 0; i < candidates.length; i++) {
      const candidatePid = candidates[i]
      const usePackage =
        ['14', '36'].includes(String(candidatePid)) || String(candidatePid).startsWith('14-')
          ? effectivePackageName
          : null
      const pidBaselines = getPidBaselineMap(
        countryBaselines,
        candidatePid,
        usePackage,
      )
      const mediaBaselineMaps = getMediaBaselineMaps(pidBaselines, mediaSource)

      for (const { baselineMap, sourceMediaSource, isFallback } of mediaBaselineMaps) {
        const { latestStartDate, value } = getLatestBaselineEntry(baselineMap)
        if (value === null) continue

        const isGroupFallback = candidatePid !== requestedPid && i > 0
        const isMediaFallback = isFallback
        const isDefault =
          isGroupFallback ||
          isMediaFallback ||
          isCountryFallback ||
          (scopedLatestDate && latestStartDate < scopedLatestDate)

        return {
          value,
          isDefault,
          sourcePid: candidatePid,
          sourceMediaSource,
          isGroupFallback,
          isMediaFallback,
          isCountryFallback,
        }
      }
    }
  }

  return emptyBaselineResult(targetDay)
}

/**
 * 获取在指定日期生效的 DR baseline 值（时间降级逻辑，用于 Tooltip 显示变化）
 * 具体国家的渠道和 total 时间线优先于 `all`。
 * group 兜底仅用于聚合 pid。
 * @returns {number} - 生效的 baseline 值
 */
export function getDrBaselineAtDate(
  baselines,
  targetDay,
  country,
  pid,
  date,
  packageName = null,
  mediaSource = DEFAULT_MEDIA_SOURCE,
) {
  const parsed = parsePid(pid)
  const effectivePackageName =
    packageName !== null ? packageName : parsed.packageName
  if (parsed.basePid === '36' && effectivePackageName === 'bus') return null
  const candidates = resolveBaselinePidCandidates(pid)

  if (
    candidates.length === 0 ||
    !baselines ||
    !baselines[targetDay]
  ) {
    return getDefaultBaseline(targetDay)
  }

  const countrySources = getCountryBaselineSources(baselines[targetDay], country)

  for (const { countryBaselines } of countrySources) {
    for (const candidatePid of candidates) {
      const usePackage =
        ['14', '36'].includes(String(candidatePid)) || String(candidatePid).startsWith('14-')
          ? effectivePackageName
          : null
      const pidBaselines = getPidBaselineMap(
        countryBaselines,
        candidatePid,
        usePackage,
      )
      const mediaBaselineMaps = getMediaBaselineMaps(pidBaselines, mediaSource)

      for (const { baselineMap } of mediaBaselineMaps) {
        const { value } = getBaselineEntryAtDate(baselineMap, date)
        if (value !== null) return value
      }
    }
  }

  return getDefaultBaseline(targetDay)
}

/**
 * 解析 pid 字符串，支持 "14-samoyed" 格式
 * @param {string} pid - pid 字符串
 * @returns {object} - { basePid: string, packageName: string | null }
 */
export function parsePid(pid) {
  if (!pid) {
    return { basePid: null, packageName: null }
  }

  const pidStr = String(pid)
  const parts = pidStr.split('-')

  if (parts.length >= 2 && parts[0] === '14') {
    return {
      basePid: '14',
      packageName: parts.slice(1).join('-'),
    }
  }

  return {
    basePid: pidStr,
    packageName: null,
  }
}

/**
 * 获取指定 pid/country/day 的所有 baseline 历史记录
 * 使用第一个有数据的 country+pid+media map。具体国家优先于 `all`，
 * 请求渠道优先于 total。
 * @returns {Array<{date: string, value: number}>} - 按日期排序的历史记录
 */
export function getDrBaselineHistory(
  baselines,
  targetDay,
  country,
  pid,
  packageName = null,
  mediaSource = DEFAULT_MEDIA_SOURCE,
) {
  const parsed = parsePid(pid)
  const effectivePackageName =
    packageName !== null ? packageName : parsed.packageName
  const candidates = resolveBaselinePidCandidates(pid)

  if (
    candidates.length === 0 ||
    !baselines ||
    !baselines[targetDay]
  ) {
    return []
  }

  const countrySources = getCountryBaselineSources(baselines[targetDay], country)

  for (const { countryBaselines } of countrySources) {
    for (const candidatePid of candidates) {
      const usePackage =
        ['14', '36'].includes(String(candidatePid)) || String(candidatePid).startsWith('14-')
          ? effectivePackageName
          : null
      const pidBaselines = getPidBaselineMap(
        countryBaselines,
        candidatePid,
        usePackage,
      )
      const mediaBaselineMaps = getMediaBaselineMaps(pidBaselines, mediaSource)

      const pidBaselineDates = mediaBaselineMaps.find(({ baselineMap }) =>
        isDateMap(baselineMap),
      )?.baselineMap

      if (!pidBaselineDates) continue

      return Object.entries(pidBaselineDates)
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => b.date.localeCompare(a.date))
    }
  }

  return []
}

/**
 * 获取在指定日期生效的 ROAS baseline 值
 * @param {object} roasLine - 从后端获取的 ROAS baseline 数据，格式为 { "0": { "2025-01-01": 0.5, ... }, "3": { "2025-01-01": 0.45, ... } }
 * @param {number} targetDay - 目标天数 (0, 3, 7, 14, 30, ...)
 * @param {string} date - 当前日期 (YYYY-MM-DD 格式的字符串，如 "2025-11-01")
 * @returns {number|null} - 生效的 baseline 值；长线未配置时返回 null
 */
export function getRoasBaseline(roasLine, targetDay, date) {
  if (!roasLine || !roasLine[targetDay]) {
    return Object.prototype.hasOwnProperty.call(DEFAULT_BASELINES, targetDay)
      ? DEFAULT_BASELINES[targetDay]
      : null
  }

  const dayBaselines = roasLine[targetDay]
  let effectiveBaseline = null
  let latestStartDateNum = -1

  // 将 date (YYYY-MM-DD) 转换为 YYYYMMDD 数字
  const currentDateNum = parseInt(date.replace(/-/g, ''), 10)

  for (const startDate in dayBaselines) {
    // 将 startDate (YYYY-MM-DD) 转换为 YYYYMMDD 数字
    const startDateNum = parseInt(startDate.replace(/-/g, ''), 10)

    if (startDateNum <= currentDateNum) {
      if (startDateNum > latestStartDateNum) {
        latestStartDateNum = startDateNum
        effectiveBaseline = dayBaselines[startDate]
      }
    }
  }

  if (effectiveBaseline !== null) {
    return effectiveBaseline
  }

  return Object.prototype.hasOwnProperty.call(DEFAULT_BASELINES, targetDay)
    ? DEFAULT_BASELINES[targetDay]
    : null
}

/**
 * Resolve ROAS baseline at a date.
 * Prefer channel-owned startDates over total-fallback startDates even when total is newer.
 */
export function resolveRoasBaseline(roasLine, totalFallbackMap, targetDay, date) {
  if (typeof roasLine === 'number') {
    return {
      value: targetDay === 0 ? roasLine : null,
      isTotalFallback: false,
    }
  }

  if (!roasLine || !roasLine[targetDay]) {
    return {
      value: Object.prototype.hasOwnProperty.call(DEFAULT_BASELINES, targetDay)
        ? DEFAULT_BASELINES[targetDay]
        : null,
      isTotalFallback: false,
    }
  }

  const dayBaselines = roasLine[targetDay]
  const currentDateNum = parseInt(String(date || '').replace(/-/g, ''), 10)

  let ownedBaseline = null
  let ownedStartDate = ''
  let ownedStartDateNum = -1
  let fallbackBaseline = null
  let fallbackStartDate = ''
  let fallbackStartDateNum = -1

  for (const startDate in dayBaselines) {
    const startDateNum = parseInt(startDate.replace(/-/g, ''), 10)
    if (Number.isNaN(currentDateNum) || startDateNum > currentDateNum) continue

    const isFallback = Boolean(totalFallbackMap?.[targetDay]?.[startDate])
    if (isFallback) {
      if (startDateNum > fallbackStartDateNum) {
        fallbackStartDateNum = startDateNum
        fallbackStartDate = startDate
        fallbackBaseline = dayBaselines[startDate]
      }
    } else if (startDateNum > ownedStartDateNum) {
      ownedStartDateNum = startDateNum
      ownedStartDate = startDate
      ownedBaseline = dayBaselines[startDate]
    }
  }

  if (ownedBaseline !== null) {
    return {
      value: ownedBaseline,
      isTotalFallback: false,
      startDate: ownedStartDate,
    }
  }

  if (fallbackBaseline !== null) {
    return {
      value: fallbackBaseline,
      isTotalFallback: true,
      startDate: fallbackStartDate,
    }
  }

  return {
    value: Object.prototype.hasOwnProperty.call(DEFAULT_BASELINES, targetDay)
      ? DEFAULT_BASELINES[targetDay]
      : null,
    isTotalFallback: false,
  }
}

export function getDisplayableRoasBaseline(
  roasLine,
  totalFallbackMap,
  targetDay,
  date,
  fallbackMode,
) {
  const resolved = resolveRoasBaseline(
    roasLine,
    totalFallbackMap,
    targetDay,
    date,
  )

  if (fallbackMode === 'hide' && resolved.isTotalFallback) {
    return null
  }

  return resolved.value
}

export function getRoasBaselineSource(
  totalFallbackMap,
  targetDay,
  startDate,
  mediaSource,
) {
  if (totalFallbackMap?.[targetDay]?.[startDate]) {
    return DEFAULT_MEDIA_SOURCE
  }

  return normalizeMediaSource(mediaSource)
}

/**
 * 获取在指定日期生效的 Cost baseline 值
 * @param {object} baselines - 从后端获取的完整 baseline 数据，格式为 { "country": { "pid": { "2025-01-01": 1000, ... } } }
 * @param {string} country - 国家代码
 * @param {string} pid - 产品ID
 * @param {string} date - 当前日期 (YYYYMMDD 格式的字符串)
 * @returns {number} - 生效的 baseline 值，如果没有则返回 0
 */
export function getCostBaseline(baselines, country, pid, date) {
  if (
    !pid ||
    !baselines ||
    !baselines[country] ||
    !baselines[country][pid]
  ) {
    return 0
  }

  const pidBaselines = baselines[country][pid]

  let effectiveBaseline = null
  let latestStartDateNum = -1

  // 将 date (YYYYMMDD) 直接转为数字
  const currentDateNum = parseInt(date, 10)

  for (const startDate in pidBaselines) {
    // 将 startDate (YYYY-MM-DD) 转换为 YYYYMMDD 数字
    const startDateNum = parseInt(startDate.replace(/-/g, ''), 10)

    if (startDateNum <= currentDateNum) {
      if (startDateNum > latestStartDateNum) {
        latestStartDateNum = startDateNum
        effectiveBaseline = pidBaselines[startDate]
      }
    }
  }

  return effectiveBaseline !== null ? effectiveBaseline : 0
}

/**
 * 获取在指定日期生效的 Profit baseline 值
 * @param {object} baselines - 从后端获取的完整 baseline 数据，格式为 { "country": { "pid": { "2025-01-01": 500, ... } } }
 * @param {string} country - 国家代码
 * @param {string} pid - 产品ID
 * @param {string} date - 当前日期 (YYYYMMDD 格式的字符串)
 * @returns {number} - 生效的 baseline 值，如果没有则返回 0
 */
export function getProfitBaseline(baselines, country, pid, date) {
  if (
    !pid ||
    !baselines ||
    !baselines[country] ||
    !baselines[country][pid]
  ) {
    return 0
  }

  const pidBaselines = baselines[country][pid]

  let effectiveBaseline = null
  let latestStartDateNum = -1

  // 将 date (YYYYMMDD) 直接转为数字
  const currentDateNum = parseInt(date, 10)

  for (const startDate in pidBaselines) {
    // 将 startDate (YYYY-MM-DD) 转换为 YYYYMMDD 数字
    const startDateNum = parseInt(startDate.replace(/-/g, ''), 10)

    if (startDateNum <= currentDateNum) {
      if (startDateNum > latestStartDateNum) {
        latestStartDateNum = startDateNum
        effectiveBaseline = pidBaselines[startDate]
      }
    }
  }

  return effectiveBaseline !== null ? effectiveBaseline : 0
}
